import { isAfter } from 'date-fns';
import { sortBy } from 'lodash';
import { CalculsGlobal } from '../../../constants/calculs';
import { CalculsFormCalendrierAssistance } from '../../../constants/calculs/calculsFormCalendrierAssistance';
import { getAgesLastArrerage } from '../../../helpers/bareme/bareme';
import {
  getAgeDateAttribution,
  getCapitalisationCoefficient,
} from '../../../helpers/prejudices/capitalisation';
import { Bareme, BaremeCapitalisationValue } from '../../../types/bareme.type';
import { MonetaryErosion } from '../../../types/monetaryErosion.type';
import {
  PrejudiceFormCalendrierAssistanceCapitalisation,
  PrejudiceFormCalendrierAssistanceRow,
} from '../../../types/prejudice.type';
import { Procedure } from '../../../types/procedure.type';
import { Victime } from '../../../types/victime.type';
import {
  getDefaultFieldValueIfEdited,
  prejudiceBaseDefaultValues,
} from '../defaultValues';
import {
  defaultTableAssistanceRowAEchoir,
  defaultTableAssistanceRowEchu,
} from './FormCalendrierAssistance.defaultValues';

export type CalendrierAssistanceCapitalisationExistingRow =
  Partial<PrejudiceFormCalendrierAssistanceRow>;
export type CalendrierAssistanceCapitalisationExistingValues = Omit<
  Partial<PrejudiceFormCalendrierAssistanceCapitalisation>,
  'rows'
> & {
  rows?: CalendrierAssistanceCapitalisationExistingRow[];
};

export const calendrierAssistanceCapitalisationDefaultValues = (params: {
  monetaryErosions: MonetaryErosion[];
  baremes: Bareme[];
  victime: Victime;
  procedure: Procedure;
  values: CalendrierAssistanceCapitalisationExistingValues | undefined;
  isExtractedValues: boolean;
}): PrejudiceFormCalendrierAssistanceCapitalisation => {
  const {
    baremes,
    victime,
    procedure,
    values,
    monetaryErosions,
    isExtractedValues,
  } = params;
  const dateLiquidation = procedure.dateLiquidation
    ? new Date(procedure.dateLiquidation)
    : undefined;
  const dateNaissance = victime.dateNaissance
    ? new Date(victime.dateNaissance)
    : undefined;
  const dateDeces = victime.dateDeces ? new Date(victime.dateDeces) : undefined;
  const sexe = victime.sexe;
  const baseValues = prejudiceBaseDefaultValues({ values });
  const notes = values?.notes ?? '';
  const forfaitHoraire =
    (values && !isExtractedValues ? values?.forfaitHoraire : 0) || 0;
  const revalorisationCoefficientsType =
    values?.revalorisationCoefficientsType ?? 'smic';
  const commentaires = values?.commentaires ?? '';
  const existingValues = {
    ...baseValues,
    notes,
    forfaitHoraire,
    revalorisationCoefficientsType,
    commentaires,
  } as const;
  const rows = sortBy(values?.rows, (row) => row.dateDebut)?.map((row) => {
    const rowType =
      row.type ||
      (row.dateDebut && dateLiquidation
        ? isAfter(new Date(row.dateDebut), dateLiquidation)
          ? 'aEchoir'
          : 'echu'
        : 'echu');
    return rowType === 'echu'
      ? defaultTableAssistanceRowEchu({
          procedure,
          existingValues,
          existingRow: row,
          isCalculCapitalisation: true,
        })
      : defaultTableAssistanceRowAEchoir({
          procedure,
          existingValues,
          existingRow: row,
        });
  });
  /* Calculs totaux */
  const totaux = CalculsFormCalendrierAssistance.totaux({
    rows,
    partResponsabilite: procedure.partResponsabilite,
    tiersPayeurs: procedure.tiersPayeurs,
    dateLiquidation,
    monetaryErosions: monetaryErosions,
    revalorisationCoefficientsType,
  });

  const totalMontantTotalAnnualise =
    getDefaultFieldValueIfEdited({
      values,
      field: 'totalMontantTotalAnnualise',
      defaultValue: totaux.montantTotalAnnualise.value,
    }) ?? 0;

  /* Capitalisation */
  const baremeId =
    (values && !isExtractedValues
      ? values?.baremeCapitalisation
      : procedure.baremes?.baremeCapitalisationId) ?? baremes[0]?._id;
  const bareme = baremes.find((bareme) => bareme._id === baremeId);
  const ageDateAttribution =
    (values?.ageDateAttribution ||
      getAgeDateAttribution({
        dateNaissance,
        dateLiquidation,
        dateDeces,
      })?.age) ??
    0;

  const baremeValues = (bareme?.values as BaremeCapitalisationValue[]) ?? [];
  const agesDernierArrerage =
    ageDateAttribution || ageDateAttribution === 0
      ? getAgesLastArrerage(baremeValues, sexe, ageDateAttribution)
      : undefined;
  const ageDernierArrerage =
    values?.ageDernierArrerage ?? agesDernierArrerage?.ages[0] ?? null;
  const isLastArrerageViager = values?.isLastArrerageViager ?? false;

  const victimeSommeACapitaliser =
    getDefaultFieldValueIfEdited({
      values,
      field: 'victimeSommeACapitaliser',
      defaultValue: totaux.montantTotalAnnualise.value,
    }) ?? 0;
  const calculatedCoefficientCapitalisation = dateNaissance
    ? getCapitalisationCoefficient({
        sexe,
        baremeValues,
        ageDateAttribution,
        ageDernierArrerage,
        isDernierArrerageViager: isLastArrerageViager,
        dateNaissance,
        dateLiquidation,
      })
    : null;

  const victimeCoefficientCapitalisation =
    getDefaultFieldValueIfEdited({
      values,
      field: 'victimeCoefficientCapitalisation',
      defaultValue: calculatedCoefficientCapitalisation,
    }) ?? null;
  const calculatedVictimeTotalCapitalise = CalculsGlobal.getMontantCapitalise(
    victimeSommeACapitaliser,
    victimeCoefficientCapitalisation,
  );

  const victimeTotalCapitalise =
    getDefaultFieldValueIfEdited({
      values,
      field: 'victimeTotalCapitalise',
      defaultValue: calculatedVictimeTotalCapitalise,
    }) || 0;

  const capitalisationTiersPayeurs = {
    parTiersPayeur: procedure.tiersPayeurs.map((tiersPayeur, index) => {
      const existingCapitalisationTiersPayeur =
        values?.capitalisationTiersPayeurs?.parTiersPayeur?.find(
          (row) => row.tiersPayeur === tiersPayeur,
        );
      const sommeACapitaliser =
        existingCapitalisationTiersPayeur?.sommeACapitaliser ?? 0;
      const coefficient = existingCapitalisationTiersPayeur?.coefficient ?? 0;
      const calculatedMontantCapitalise = CalculsGlobal.getMontantCapitalise(
        sommeACapitaliser,
        coefficient,
      );
      const montantCapitalise =
        getDefaultFieldValueIfEdited({
          values,
          field: `capitalisationTiersPayeurs.parTiersPayeur.${index}.montantCapitalise`,
          defaultValue: calculatedMontantCapitalise,
        }) ?? 0;
      return {
        tiersPayeur,
        sommeACapitaliser,
        coefficient,
        montantCapitalise,
      };
    }),
  };
  const calculatedTiersPayeursTotalCapitalise = CalculsGlobal.sum(
    capitalisationTiersPayeurs.parTiersPayeur.map(
      (tiersPayeur) => tiersPayeur.montantCapitalise,
    ),
  );
  const tiersPayeursTotalCapitalise =
    getDefaultFieldValueIfEdited({
      values,
      field: 'tiersPayeursTotalCapitalise',
      defaultValue: calculatedTiersPayeursTotalCapitalise,
    }) ?? 0;

  const rentes = {
    montant: values?.rentes?.montant ?? 0,
  };
  const isRentesOption = values?.isRentesOption ?? false;
  return {
    ...existingValues,
    rows,
    totalMontantTotalAnnualise,
    ageDateAttribution,
    ageDernierArrerage,
    isLastArrerageViager,
    victimeSommeACapitaliser,
    victimeCoefficientCapitalisation,
    victimeTotalCapitalise,
    capitalisationTiersPayeurs,
    tiersPayeursTotalCapitalise,
    rentes,
    isRentesOption,
  };
};
